import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Typography, 
  Box, 
  Button, 
  Alert, 
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Toolbar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { 
  Add as AddIcon, 
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  Flag as PriorityIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as ImportIcon,
  Download as ExportIcon,
  Visibility as ViewIcon,
  DeleteOutline as DeleteOutlineIcon,
  LocalShipping as DeliveryIcon,
  Assignment as PickupIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVRPData } from '../contexts/VRPDataContext';
import { 
  useJobs, 
  useAvailableLocationsForJobs,
  useDeleteJob,
  type Job 
} from '../hooks/useJobs';
import { useLocations } from '../hooks/useLocations';
import { JobForm } from '../components/jobs/JobForm';
import { BulkImportDialog } from '../components/bulk/BulkImportDialog';
import BulkExportDialog from '../components/bulk/BulkExportDialog';
import { formatJobTimePair } from '../utils/timeFormat';

export const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isBulkExportOpen, setIsBulkExportOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingJob, setViewingJob] = useState<Job | null>(null);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { currentScenario } = null /* TODO: Replace with Toolpad navigation */;
  const { currentDataset } = useVRPData();
  const queryClient = useQueryClient();  
  
  // CRITICAL: Load locations first (master data dependency) with scenario/dataset context
  const { 
    data: locations, 
    isLoading: locationsLoading, 
    error: locationsError 
  } = useLocations(currentProject?.id || 0, currentScenario?.id, { 
    limit: 100,
    dataset_id: currentDataset?.id
  });

  // Get available locations for jobs (with coordinates) - using context-aware data
  const {
    data: availableLocations,
    isLoading: availableLocationsLoading,
    hasLocations
  } = useAvailableLocationsForJobs(currentProject?.id || 0, currentScenario?.id, {
    dataset_id: currentDataset?.id
  });

  // DEPENDENT QUERY: Jobs load with scenario/dataset context
  const { 
    data: jobs, 
    isLoading: jobsLoading, 
    error: jobsError 
  } = useJobs(currentProject?.id || 0, currentScenario?.id, { 
    limit: 100,
    dataset_id: currentDataset?.id
  });

  const { mutate: deleteJob } = useDeleteJob();

  // Debug logging for context state
  useEffect(() => {
    console.log('💼 [Jobs] Context state:', {
      currentProject: currentProject?.name || 'none',
      currentScenario: currentScenario?.name || 'none',
      currentDataset: currentDataset?.name || 'none',
      jobsCount: jobs?.length || 0,
      locationsCount: locations?.length || 0,
      hasContext: !!(currentScenario && currentDataset),
      queryContext: {
        projectId: currentProject?.id,
        scenarioId: currentScenario?.id,
        datasetId: currentDataset?.id
      }
    });
  }, [currentProject, currentScenario, currentDataset, jobs, locations]);

  const handleOpenForm = () => setIsFormOpen(true);
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingJob(null);
  };
  const handleBackToDashboard = () => navigate('/dashboard');

  const handleJobMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, job: Job) => {
    setAnchorEl(event.currentTarget);
    setSelectedJob(job);
  };

  const handleJobMenuClose = () => {
    setAnchorEl(null);
    setSelectedJob(null);
  };

  // Handle successful import - refresh data
  const handleImportSuccess = () => {
    // Invalidate and refetch jobs data
    queryClient.invalidateQueries({ queryKey: ['jobs', currentProject?.id] });
    setIsBulkImportOpen(false);
    
    console.log('🔄 Jobs page: Refreshing jobs data after successful import');
  };

  const handleViewJob = (job?: Job) => {
    const jobToView = job || selectedJob;
    if (jobToView) {
      setViewingJob(jobToView);
      setViewDialogOpen(true);
    }
    handleJobMenuClose();
  };

  const handleEditJob = (job?: Job) => {
    const jobToEdit = job || selectedJob;
    if (jobToEdit) {
      console.log('Edit job:', jobToEdit.id);
      setEditingJob(jobToEdit);
      setIsFormOpen(true);
    }
    handleJobMenuClose();
  };

  const handleDeleteJob = (job?: Job) => {
    const jobToDelete = job || selectedJob;
    if (jobToDelete && currentProject) {
      if (window.confirm(`Are you sure you want to delete job "${jobToDelete.description}"?`)) {
        deleteJob({ 
          id: jobToDelete.id, 
          projectId: currentProject.id 
        });
      }
    }
    handleJobMenuClose();
  };

  const handleSelectRow = (jobId: number) => {
    setSelectedRows(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && jobs) {
      setSelectedRows(jobs.map(job => job.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleBulkAction = async () => {
    console.log('🔥 Jobs bulk action called:', { bulkAction, selectedRows, currentProject });
    if (!bulkAction || selectedRows.length === 0 || !jobs || !currentProject) return;
    
    if (bulkAction === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selectedRows.length} job(s)?`)) {
        try {
          // Delete jobs one by one using the mutation function directly
          for (const jobId of selectedRows) {
            await new Promise<void>((resolve, reject) => {
              deleteJob(
                { id: jobId, projectId: currentProject.id },
                {
                  onSuccess: () => resolve(),
                  onError: (error) => reject(error)
                }
              );
            });
          }
          
          setSelectedRows([]);
          setBulkAction('');
          console.log('✅ Jobs bulk delete completed successfully');
        } catch (error) {
          console.error('❌ Jobs bulk delete failed:', error);
          alert('Some jobs could not be deleted. Please try again.');
        }
      }
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 5: return 'error';
      case 4: return 'warning';
      case 3: return 'info';
      case 2: return 'primary';
      case 1: return 'default';
      default: return 'default';
    }
  };
  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 5: return 'Urgent';
      case 4: return 'High';
      case 3: return 'Medium';
      case 2: return 'Low';
      case 1: return 'Very Low';
      default: return 'Normal';
    }
  };

  // Show project selection message if no current project
  if (!currentProject) {
    return (
      <ProfessionalLayout
        pageTitle="Jobs"
        pageSubtitle="Please select a project to view jobs"
        activeTab="Jobs"
        actionButtons={
          <Button 
            variant="contained"
            onClick={() => navigate('/projects')}
            startIcon={<ArrowBackIcon />}
          >
            Select Project
          </Button>
        }
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>No Project Selected</Typography>
          <Typography variant="body1">Please select a project from the dashboard to view its jobs.</Typography>
          <Button variant="outlined" onClick={handleBackToDashboard} sx={{ mt: 2 }} startIcon={<ArrowBackIcon />}>
            Back to Dashboard
          </Button>
        </Alert>
      </ProfessionalLayout>
    );
  }

  // Show locations loading (master data dependency)
  if (locationsLoading || availableLocationsLoading) {
    return (
      <ProfessionalLayout
        pageTitle="Jobs"
        pageSubtitle={`Loading location master data for ${currentProject.name}...`}
        activeTab="Jobs"
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading location master data for {currentProject.name}...
          </Typography>
        </Box>
      </ProfessionalLayout>
    );
  }

  // Handle locations error (critical dependency)
  if (locationsError) {
    return (
      <ProfessionalLayout
        pageTitle="Jobs"
        pageSubtitle="Error loading location master data"
        activeTab="Jobs"
        actionButtons={
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        }
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error Loading Location Master Data</Typography>
          <Typography variant="body1">Jobs require location master data to function properly.</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {locationsError instanceof Error ? locationsError.message : 'Unknown error occurred'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleBackToDashboard} sx={{ mr: 2 }} startIcon={<ArrowBackIcon />}>
              Back to Dashboard
            </Button>
            <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
          </Box>
        </Alert>
      </ProfessionalLayout>
    );
  }
  // Main render - Jobs with location master integration and context
  const pageSubtitle = (() => {
    if (!currentProject?.name) return 'Manage jobs';
    
    let subtitle = `Manage jobs for ${currentProject.name}`;
    if (currentScenario?.name) {
      subtitle += ` • Scenario: ${currentScenario.name}`;
    }
    if (currentDataset?.name) {
      subtitle += ` • Dataset: ${currentDataset.name}`;
    }
    return subtitle;
  })();

  return (
    <ProfessionalLayout
      pageTitle="Jobs"
      pageSubtitle={pageSubtitle}
      activeTab="Jobs"
      actionButtons={
        <>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setIsBulkImportOpen(true)}
            sx={{ mr: 1 }}
          >
            Import
          </Button>
          {jobs && jobs.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => setIsBulkExportOpen(true)}
              sx={{ mr: 1 }}
            >
              Export
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
          >
            New Job
          </Button>
        </>
      }
    >

      {/* Context Warning Alert */}
      {(!currentScenario || !currentDataset) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Incomplete Context</Typography>
          <Typography variant="body2">
            {!currentScenario && !currentDataset 
              ? 'Please select a scenario and dataset to view job data.'
              : !currentScenario 
              ? 'Please select a scenario to proceed.'
              : 'Please select a dataset to load job data.'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Navigate to: Project → Scenarios → Data Management → Jobs
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/scenarios')}
              size="small"
              sx={{ mr: 1 }}
            >
              Go to Scenarios
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/data-management')}
              size="small"
            >
              Go to Data Management
            </Button>
          </Box>
        </Alert>
      )}

      {/* Jobs Loading State */}
      {jobsLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>Loading jobs...</Typography>
        </Box>
      )}

      {/* Jobs Error State */}
      {jobsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error Loading Jobs</Typography>
          <Typography variant="body2">
            {jobsError instanceof Error ? jobsError.message : 'Unknown error occurred'}
          </Typography>
        </Alert>
      )}

      {/* Jobs List */}
      {jobs && jobs.length > 0 && (
        <Box>
          {/* Bulk Actions Toolbar */}
          {selectedRows.length > 0 && (
            <Toolbar sx={{ mb: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {selectedRows.length} job{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
              <FormControl sx={{ minWidth: 120, mr: 2 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={bulkAction}
                  label="Action"
                  onChange={(e) => setBulkAction(e.target.value)}
                  size="small"
                >
                  <MenuItem value="delete">Delete</MenuItem>
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleBulkAction}
                disabled={!bulkAction}
                startIcon={<DeleteOutlineIcon />}
              >
                Apply
              </Button>
            </Toolbar>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < jobs.length}
                      checked={jobs.length > 0 && selectedRows.length === jobs.length}
                      onChange={handleSelectAllRows}
                    />
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Service Time</TableCell>
                  <TableCell>Delivery/Pickup</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => {
                  const jobLocation = availableLocations?.find(loc => loc.id === job.location_id);
                  
                  return (
                    <TableRow 
                      key={job.id}
                      hover
                      selected={selectedRows.includes(job.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(job.id)}
                          onChange={() => handleSelectRow(job.id)}
                        />
                      </TableCell>
                      <TableCell 
                        onClick={() => handleViewJob(job)}
                        sx={{ fontWeight: 'medium', color: 'primary.main' }}
                      >
                        <Box display="flex" alignItems="center">
                          <WorkIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                          {job.description}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {job.priority ? (
                          <Chip 
                            icon={<PriorityIcon />}
                            label={getPriorityLabel(job.priority)}
                            color={getPriorityColor(job.priority) as any}
                            size="small"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {jobLocation?.name || `Location ID: ${job.location_id}`}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const times = formatJobTimePair(job.setup || 0, job.service || 0);
                          return (
                            <>
                              <Typography variant="body2">
                                Setup: {times.setup}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Service: {times.service}
                              </Typography>
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {job.delivery?.length > 0 && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <DeliveryIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                            <Typography variant="caption">
                              [{job.delivery.join(', ')}]
                            </Typography>
                          </Box>
                        )}
                        {job.pickup?.length > 0 && (
                          <Box display="flex" alignItems="center">
                            <PickupIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                            <Typography variant="caption">
                              [{job.pickup.join(', ')}]
                            </Typography>
                          </Box>
                        )}
                        {(!job.delivery?.length && !job.pickup?.length) && '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewJob(job);
                          }}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditJob(job);
                          }}
                          title="Edit Job"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteJob(job);
                          }}
                          title="Delete Job"
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Empty State */}
      {jobs && jobs.length === 0 && !jobsLoading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Jobs Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {currentScenario && currentDataset 
              ? `No jobs found in dataset "${currentDataset.name}" for scenario "${currentScenario.name}". Click "New Job" to add your first job.`
              : 'No jobs have been created for this project yet. Click "New Job" to add your first job.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
            sx={{ background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)' }}
          >
            Add First Job
          </Button>
        </Paper>
      )}

      {/* Context Information for empty dataset */}
      {currentScenario && currentDataset && jobs && jobs.length === 0 && !jobsLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            No jobs found in <strong>{currentDataset.name}</strong> for scenario <strong>{currentScenario.name}</strong>.
            You can add jobs using the "New Job" button or import them from a CSV file.
          </Typography>
        </Alert>
      )}

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <WorkIcon sx={{ mr: 1 }} />
            Job Details: {viewingJob?.description}
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingJob && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>ID:</strong> {viewingJob.id}
              </Typography>
              {viewingJob.priority && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">
                    <strong>Priority:</strong>
                  </Typography>
                  <Chip 
                    icon={<PriorityIcon />}
                    label={getPriorityLabel(viewingJob.priority)}
                    color={getPriorityColor(viewingJob.priority) as any}
                    size="small"
                  />
                </Box>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Location:</strong> {availableLocations?.find(loc => loc.id === viewingJob.location_id)?.name || `Location ID: ${viewingJob.location_id}`}
              </Typography>
              {(() => {
                const times = formatJobTimePair(viewingJob.setup || 0, viewingJob.service || 0);
                return (
                  <>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Setup Time:</strong> {times.setupLong}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Service Time:</strong> {times.serviceLong}
                    </Typography>
                  </>
                );
              })()}
              {viewingJob.delivery && viewingJob.delivery.length > 0 && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Delivery:</strong> [{viewingJob.delivery.join(', ')}]
                </Typography>
              )}
              {viewingJob.pickup && viewingJob.pickup.length > 0 && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Pickup:</strong> [{viewingJob.pickup.join(', ')}]
                </Typography>
              )}
              {viewingJob.skills && viewingJob.skills.length > 0 && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Required Skills:</strong> {viewingJob.skills.join(', ')}
                </Typography>
              )}
              {viewingJob.time_windows && viewingJob.time_windows.length > 0 && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Time Windows:</strong> 
                  {viewingJob.time_windows.map((tw, index) => (
                    <span key={index}>
                      {index > 0 && ', '}
                      {Math.floor(tw[0] / 3600).toString().padStart(2, '0')}:
                      {Math.floor((tw[0] % 3600) / 60).toString().padStart(2, '0')} - 
                      {Math.floor(tw[1] / 3600).toString().padStart(2, '0')}:
                      {Math.floor((tw[1] % 3600) / 60).toString().padStart(2, '0')}
                    </span>
                  ))}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button 
            variant="outlined" 
            onClick={() => {
              setViewDialogOpen(false);
              handleEditJob(viewingJob);
            }}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Form Dialog - INTEGRATED WITH LOCATION MASTER */}
      <JobForm
        open={isFormOpen}
        onClose={handleCloseForm}
        projectId={currentProject?.id || 0}
        job={editingJob}
      />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onSuccess={handleImportSuccess}
        entityType="jobs"
        projectId={currentProject?.id || 0}
        availableLocations={availableLocations || []}
      />

      {/* Bulk Export Dialog */}
      <BulkExportDialog
        open={isBulkExportOpen}
        onClose={() => setIsBulkExportOpen(false)}
        entityType="jobs"
        data={jobs || []}
        projectName={currentProject?.name || 'Unknown'}
      />
    </ProfessionalLayout>
  );
};